import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ToolMarquee, TOOLS, Tile } from './ToolMarquee';
import { PixelCursor } from './PixelCursor';
const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * 'gone' is the resting state: once the connection has landed the control has
 * said everything it has to say, so it leaves. What remains looping is the
 * directory — the connections themselves, which are the durable claim.
 */
/**
 * The beats of one click, in order. Every one of them exists because a real
 * click has it: you move to the thing, the thing acknowledges you, you press,
 * it works, it hands over. Skipping the approach and the acknowledgement is
 * what made this read as things happening at random rather than as somebody
 * doing something.
 */
type Phase = 'idle' | 'hover' | 'press' | 'connecting' | 'gone';

/**
 * Beat timings, in ms from the moment the stage enters view.
 *
 * Roughly halved from the original pacing. The story is a preamble to the
 * thing the section is actually about — the directory and the Connections
 * screen — so every beat it holds is a beat the reader spends looking at an
 * empty band. Each is now the shortest duration that still reads as its own
 * event: the approach, the beat of intent before the press, the press
 * bottoming out, and just enough spinner to believe work happened.
 */
const T_HOVER = 240; // pointer has travelled to the control
const T_PRESS = T_HOVER + 280; // a beat of hover BEFORE the press — the pause is what makes it read as intent
const T_CONNECT = T_PRESS + 110; // the press bottoms out, then the work starts
const T_GONE = T_CONNECT + 420; // long enough to believe, short enough not to wait

/* NO "CONNECTED" BEAT. The control used to settle into a dark glass pill with
 a green check and hold it for a second before leaving. That state said
 nothing the arriving directory does not already say — the tools flooding in
 IS the confirmation — so it was a second, quieter confirmation parked on top
 of the real one. */

/**
 * ONE CONNECTION, PLAYED ONCE.
 *
 * The section used to open with the directory band and the Connections screen
 * both already on the page, which made "connect once" a caption under two
 * finished pictures. Here the reader WATCHES the claim happen: a single
 * oversized Connect control with an unmistakable pointer beside it, a press,
 * and the whole layer unfolds — the directory first, then the screen it
 * produces.
 *
 * NOT INTERACTIVE, and not a loop. It autoplays once when the stage comes into
 * view, then the control exits and only the directory keeps moving. A button
 * the reader can press invites them to press it again, which turns a single
 * demonstrated moment into a toy; and a control left sitting on screen after
 * its moment has passed becomes furniture.
 */
export function ConnectStage({
  children
}: {
  /** The Connections screen, revealed last. */
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const [phase, setPhase] = React.useState<Phase>('idle');
  const stage = React.useRef<HTMLDivElement>(null);

  /*
   * PHONES SKIP THE STORY.
   *
   * The demonstration needs a pointer travelling to a control, and a pointer
   * is a desktop object — on a touch screen there is no cursor to recognise,
   * so the beat reads as a stray graphic drifting across the section rather
   * than as somebody clicking something. It also spends a full screen of a
   * narrow viewport on a control that then leaves. Below md the directory and
   * the screen are simply present, which is what the section is about anyway.
   */
  const [isNarrow, setIsNarrow] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const skip = reduce || isNarrow;

  // Plays on ENTERING VIEW, once. Firing on mount would spend the whole story
  // above the fold for anyone who has not scrolled here yet.
  React.useEffect(() => {
    if (skip) return;
    const el = stage.current;
    if (!el) return;
    const timers: number[] = [];
    const io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      timers.push(window.setTimeout(() => setPhase('hover'), T_HOVER), window.setTimeout(() => setPhase('press'), T_PRESS), window.setTimeout(() => setPhase('connecting'), T_CONNECT), window.setTimeout(() => setPhase('gone'), T_GONE));
    }, {
      // Fires as the band's top edge clears rather than waiting for a third of
      // the stage to be on screen — by the time the old threshold was met the
      // reader had already been looking at an empty band for a moment.
      threshold: 0.15
    });
    io.observe(el);
    return () => {
      io.disconnect();
      timers.forEach(window.clearTimeout);
    };
  }, [skip]);

  // No story: the directory and the screen are simply present.
  if (skip) {
    return <div data-id="element-821" data-mp="1rtvezpb6w94f">
        <ToolMarquee data-id="element-822" data-mp="1rtlp01q5a1yq" />
        <div className="mt-12 md:mt-16" data-id="element-823" data-mp="1rs29idcbbuy0">{children}</div>
      </div>;
  }
  const gone = phase === 'gone';
  const working = phase === 'connecting';
  const over = phase === 'hover' || phase === 'press' || working;
  // The pointer's tip, as an offset from the stage's centre. It arrives from
  // below-right, settles on the control's label, dips 3px on the press, and
  // leaves the way it came.
  const tip = phase === 'idle' ? {
    x: 150,
    y: 96,
    o: 0
  } : phase === 'hover' ? {
    x: 30,
    y: 10,
    o: 1
  } : phase === 'press' ? {
    x: 30,
    y: 13,
    o: 1
  } : {
    x: 96,
    y: 78,
    o: 0
  };
  return <div ref={stage} data-id="element-824" data-mp="104a88v61robf">
      {/* THE STAGE. A tall empty band while idle — the emptiness is what makes
       the press feel like it is about to fill something — collapsing as the
       layer unfolds into it, and closing completely once the control leaves so
       no empty gap is left above the directory. */}
      <motion.div aria-hidden="true" className="relative flex items-center justify-center overflow-hidden" animate={{
      height: gone ? 0 : 260
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} data-id="element-825" data-mp="2nzygdqwkwvgw">
        <AnimatePresence data-id="element-826" data-mp="2e20x7htktov5">
          {/* THE CONTROL ANSWERS THE POINTER. It lifts and brightens on hover,
           bottoms out on the press, and settles back while it works — the
           same three states a real button has. Without them the pointer was
           arriving at something inert, so the click had nothing to land on. */}
          {!gone && <motion.div className="relative inline-flex items-center gap-3 rounded-full font-bold text-white" style={{
          height: 68,
          paddingInline: 38,
          fontSize: 19
        }} animate={{
          scale: phase === 'press' ? 0.965 : over ? 1.025 : 1,
          y: phase === 'press' ? 0 : over ? -3 : 0,
          backgroundColor: over ? '#F2620F' : '#EA580C',
          boxShadow: over ? '0 18px 38px -18px rgba(234,88,12,0.75)' : '0 0 0 0 rgba(234,88,12,0)'
        }} transition={{
          scale: {
            duration: phase === 'press' ? 0.1 : 0.22,
            ease: 'easeOut'
          },
          y: {
            duration: 0.22,
            ease: 'easeOut'
          },
          backgroundColor: {
            duration: 0.2
          },
          boxShadow: {
            duration: 0.25
          }
        }} exit={{
          opacity: 0,
          scale: 0.94,
          transition: {
            duration: 0.3,
            ease: 'easeOut'
          }
        }} data-id="element-827" data-mp="31y544e7tdf2u">
              {/* One ring leaving the control on the press — not a pulse, which
               would read as a status light rather than a click. */}
              <AnimatePresence data-id="element-828" data-mp="13iftlbci976j">
                {phase === 'press' && <motion.span className="absolute inset-0 rounded-full ring-2 ring-[#EA580C]" initial={{
              opacity: 0.9,
              scale: 1
            }} animate={{
              opacity: 0,
              scale: 1.34
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-829" data-mp="13ftdgnbjtjcp" />}
              </AnimatePresence>

              {working ? <motion.span className="size-[19px]" animate={{
            rotate: 360
          }} transition={{
            duration: 0.6,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-830" data-mp="1xtsrhp5geghb">
                  <svg viewBox="0 0 24 24" className="size-full" fill="none" data-id="element-831" data-mp="155dmlvg1dejp">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity={0.3} strokeWidth={3} data-id="element-832" data-mp="1jtrs6e4u7h3y" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeDasharray="26 100" data-id="element-833" data-mp="1f2jg1ac4hhn3" />
                  </svg>
                </motion.span> : <svg className="size-[20px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" data-id="element-834" data-mp="rb4c5d4p6s9q">
                  <path d="M9.5 14.5 5.8 18.2a3.5 3.5 0 0 1-4.9-5l3.6-3.6M14.5 9.5l3.7-3.7a3.5 3.5 0 0 1 4.9 5l-3.6 3.6M8.8 15.2l6.4-6.4" data-id="element-835" data-mp="2y06ak0nzzhoh" />
                </svg>}

              <span className="whitespace-nowrap" data-id="element-836" data-mp="2p1ktk9y6capt">
                {working ? 'Connecting…' : 'Connect to Giga'}
              </span>
            </motion.div>}
        </AnimatePresence>

        {/* THE POINTER. Idle it drifts, so the control reads as about to be
         clicked; on the press it drops onto it and then leaves — a cursor
         parked on screen stops reading as someone doing something. */}
        {/* ONE CONTINUOUS TRAVEL, not a drift and a snap. It used to hover in
         place bobbing on a loop and then jump to the control at the moment of
         the press, which read as two unrelated movements. Now it comes in on
         a single decelerating path, stops on the label, dips for the press,
         and leaves the way it came — no loop anywhere, because a cursor that
         idles forever reads as an animation rather than as somebody arriving. */}
        {!gone && <motion.div className="pointer-events-none absolute left-1/2 top-1/2" initial={{
        opacity: 0,
        x: 150,
        y: 96
      }} animate={{
        opacity: tip.o,
        x: tip.x,
        y: tip.y
      }} transition={{
        // The approach is the long move; the press dip and the exit are quick.
        duration: phase === 'hover' ? 0.34 : phase === 'press' ? 0.09 : 0.3,
        ease: phase === 'hover' ? EASE : 'easeOut'
      }} data-id="element-837" data-mp="2ymrs2k3kni69">
            <PixelCursor className="h-[62px] w-auto drop-shadow-[0_6px_10px_rgba(0,0,0,0.45)]" data-id="element-838" data-mp="2fuffz5n492ix" />
          </motion.div>}

        {/* DECODED BEFORE THEY ARE NEEDED. Twenty-eight remote marks fetched
         at the moment the band appears is the other half of the glitch — the
         tiles land empty and fill in a frame later. Rendered in a zero-sized
         box from the first frame, the browser has the whole set decoded by
         the time the story reaches them. */}
        <div aria-hidden="true" className="pointer-events-none absolute size-0 overflow-hidden opacity-0" data-id="element-839" data-mp="f974zl8pi24k">
          {TOOLS.map(slug => <Tile key={slug} slug={slug} data-id="element-840" data-mp="a780u955716k" />)}
        </div>
      </motion.div>

      {/* THE UNFOLD. The directory first — the marks pop in from the centre
       outward so the set reads as arriving rather than as a band that was
       always there — then the screen those connections produce. */}
      <AnimatePresence data-id="element-841" data-mp="y0zx7dfybjb8">
        {gone && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.24,
        // Starts as the band finishes closing rather than crossing it, so the
        // handover is a sequence and not two things overlapping.
        delay: 0.1
      }} data-id="element-842" data-mp="t02emzr14n97">
            <ToolMarquee intro data-id="element-843" data-mp="38q6iu537ac9c" />
            <motion.div className="mt-12 md:mt-16" initial={{
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          // Overlaps the tail of the directory's arrival rather than waiting
          // for it to finish and then start moving. The screen was landing a
          // full second after the logos, which is long enough that the reader
          // reads the section as finished before its main exhibit shows up.
          delay: 0.55,
          ease: EASE
        }} data-id="element-844" data-mp="38rgj90xizwj5">
              {children}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/* THE TWO-LAYER HANDOVER IS GONE.
 *
 * The directory used to arrive as a centred row of 15 tiles laid OVER the
 * marquee and crossfaded off it. Both halves showed a different slice of the
 * directory at different x positions, so at the swap every mark jumped — the
 * "different set of logos, positioned differently" glitch. The marquee now
 * plays its own tiles in (see ToolMarquee's `intro`) and then starts moving,
 * so the marks that arrive are the marks that travel and there is no handover
 * to be smooth about.
 */